import type { v2 } from '@deque/billing-service-client';
import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Icon,
  OptionsMenu,
  OptionsMenuItem,
  OptionsMenuTrigger,
  Toast,
  Tooltip
} from '@deque/cauldron-react';
import React, {
  type Dispatch,
  type FC,
  Fragment,
  type SetStateAction,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import analyticsInstances, {
  getFallbackAnalyticsInstanceId
} from '~/common/analyticsInstances';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import type { AuthUser } from '~/common/contexts/auth';
import { useGlobalToast } from '~/common/contexts/globalToast';
import type { IntegrationProductInfo } from '~/common/utils/get-integration-product-name-from-slug';
import type { IntegrationConnection } from '~/common/utils/integrations-client/jira/connections';
import type { getActionsFn } from '../../../utils/getJiraOnPremActions';
import styles from './ConnectionActionsMenu.css';

type DeleteConnectionAlertProps = {
  showAlert: boolean;
  setShowAlert: Dispatch<SetStateAction<boolean>>;
  deleteConnection: () => void;
};
const DeleteConnectionAlert: FC<DeleteConnectionAlertProps> = ({
  showAlert,
  setShowAlert,
  deleteConnection
}) => {
  const { t } = useTranslation();

  return showAlert ? (
    <Alert heading={t('Delete connection')} show variant="warning">
      <AlertContent>
        {t(
          'Deleting this connection will also delete all templates for this connection. Are you sure you want to delete this connection?'
        )}
      </AlertContent>
      <AlertActions>
        <Button variant="error" onClick={deleteConnection}>
          {t('Delete')}
        </Button>
        <Button variant="secondary" onClick={() => setShowAlert(false)}>
          {t('Cancel')}
        </Button>
      </AlertActions>
    </Alert>
  ) : null;
};

export type ConnectionActionsMenuProps = {
  connection: IntegrationConnection;
  integrationProductInfo: IntegrationProductInfo;
  enterprise: v2.Enterprise;
  canManageIntegrations: boolean;
  authUser: AuthUser;
  connectionsUpdated: () => void;
  getActions: getActionsFn;
};

export const ConnectionActionsMenu: FC<ConnectionActionsMenuProps> = ({
  connection,
  integrationProductInfo,
  enterprise,
  canManageIntegrations,
  authUser,
  connectionsUpdated,
  getActions
}) => {
  const { t } = useTranslation();
  const optionsMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const history = useHistory();

  const analytics =
    analyticsInstances[
      getFallbackAnalyticsInstanceId(
        integrationProductInfo.integrationProductSlug
      )
    ];
  const { setContents } = useGlobalToast();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const { displayName } = integrationProductInfo;

  const actions = getActions({
    displayName,
    integrationProductInfo,
    connection,
    authUser,
    enterprise,
    analytics,
    setIsProcessing,
    setError,
    connectionsUpdated,
    setContents,
    t
  });

  if (isProcessing) {
    return <ScrimmedLoader label={t('Loading...')} />;
  }

  const errorToast = error ? (
    <Toast show type="error" onDismiss={() => setError('')}>
      {error}
    </Toast>
  ) : null;

  const isConnected = connection?.status === 'connected';

  return canManageIntegrations ? (
    <Fragment>
      {errorToast}
      <DeleteConnectionAlert
        showAlert={showAlert && !error}
        setShowAlert={setShowAlert}
        deleteConnection={actions.deleteConnection}
      />
      <div className={styles.actionIcons}>
        <OptionsMenu
          trigger={triggerProps => (
            <OptionsMenuTrigger {...triggerProps} ref={optionsMenuTriggerRef}>
              <Icon type="kabob" label={t('Options')} />
            </OptionsMenuTrigger>
          )}
        >
          {!isConnected && connection.base_url && (
            <OptionsMenuItem
              onSelect={actions.connect}
              aria-label={t('Connect to {{displayName}} {{baseUrl}}', {
                displayName,
                baseUrl: connection.base_url
              })}
            >
              {t('Connect to integration')}
            </OptionsMenuItem>
          )}
          {isConnected && (
            <OptionsMenuItem
              onSelect={actions.disconnect}
              aria-label={t('Disconnect {{displayName}} from {{baseUrl}}', {
                displayName,
                baseUrl: connection.base_url
              })}
            >
              {t('Disconnect')}
            </OptionsMenuItem>
          )}
          {!isConnected && (
            <OptionsMenuItem
              onSelect={() => setShowAlert(true)}
              aria-label={t('Delete {{displayName}} {{baseUrl}} connection', {
                displayName,
                baseUrl: connection.base_url
              })}
            >
              {t('Delete connection')}
            </OptionsMenuItem>
          )}
          <OptionsMenuItem
            onSelect={() =>
              history.push(
                `/configuration/integrations/${connection.product_slug}/settings/${connection.id}`
              )
            }
          >
            {t('Settings')}
          </OptionsMenuItem>
        </OptionsMenu>
        <Tooltip target={optionsMenuTriggerRef} placement="left" variant="info">
          {t('Additional Options')}
        </Tooltip>
      </div>
    </Fragment>
  ) : null;
};
